import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { PillButton } from "./PillButton";
import { cn } from "../lib/utils";

const linkClass = ({ isActive }) =>
  cn(
    "relative text-[13px] text-stone whitespace-nowrap transition-colors duration-300 hover:text-ink after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-sage after:transition-all after:duration-300",
    isActive ? "text-ink after:w-full" : "after:w-0 hover:after:w-full"
  );

const DesktopDropdown = ({ item }) => {
  const { pathname } = useLocation();
  const active = item.children.some((c) => c.to === pathname);
  return (
    <div className="relative group" data-testid="nav-dropdown-percorsi">
      <button
        className={cn(
          "flex items-center gap-1 text-[13px] whitespace-nowrap transition-colors duration-300 hover:text-ink",
          active ? "text-ink" : "text-stone"
        )}
      >
        {item.label}
        <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />
      </button>
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 transition-[opacity,transform,visibility] duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
        <div className="bg-cream border border-border rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.08)] p-2 min-w-[240px]">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) =>
                cn(
                  "block px-4 py-3 rounded-xl text-sm transition-colors duration-200",
                  isActive ? "bg-sand text-ink" : "text-stone hover:bg-sand hover:text-ink"
                )
              }
              data-testid={`nav-dropdown-link-${c.to.replace("/", "")}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300",
        scrolled
          ? "bg-cream/80 backdrop-blur-xl border-b border-border/60 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
          : "bg-transparent border-b border-transparent"
      )}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="group flex flex-col leading-none" data-testid="nav-logo">
          <span className="font-serif text-2xl text-ink tracking-tight">Susanna Paoletti</span>
          <span className="text-[11px] uppercase tracking-[0.22em] text-stone mt-1">
            Psicologa · Psicoterapeuta
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-4 xl:gap-6">
          {NAV_LINKS.map((l) =>
            l.children ? (
              <DesktopDropdown key={l.label} item={l} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={linkClass}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              >
                {l.label}
              </NavLink>
            )
          )}
          <PillButton to="/contatti" testid="nav-cta-desktop" className="px-4 py-2.5 whitespace-nowrap">
            Primo colloquio
          </PillButton>
        </div>

        <button
          className="lg:hidden text-ink p-2 -mr-2"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      <div
        className={cn(
          "lg:hidden overflow-y-auto bg-cream/95 backdrop-blur-xl border-b border-border transition-[max-height,opacity] duration-400 ease-out",
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((l) =>
            l.children ? (
              <div key={l.label} className="py-3 border-b border-border/60">
                <p className="text-xs uppercase tracking-[0.22em] text-stone mb-2">{l.label}</p>
                <div className="flex flex-col gap-1 pl-2">
                  {l.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      className={({ isActive }) =>
                        cn("py-2 text-lg font-serif transition-colors", isActive ? "text-sage-dark" : "text-ink")
                      }
                      data-testid={`mobile-nav-link-${c.to.replace("/", "")}`}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "py-3 text-lg font-serif border-b border-border/60 transition-colors",
                    isActive ? "text-sage-dark" : "text-ink"
                  )
                }
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              >
                {l.label}
              </NavLink>
            )
          )}
          <PillButton to="/contatti" testid="mobile-nav-cta" className="mt-5 w-full">
            Richiedi un primo colloquio
          </PillButton>
        </div>
      </div>
    </header>
  );
};
